// src/features/admin-options/components/EventNotificationConfigForm.tsx

import { useState } from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Chip,
  Typography,
  Divider,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

export interface EventNotificationPreferences {
  notifyKeyDispensed: boolean;
  notifyCardLow: boolean;
  notifyCardEmpty: boolean;
  notifyCheckInCompleted: boolean;
  notifyPreCheckInCompleted: boolean;
  notifyCriticalErrors: boolean;
}

export interface EventNotificationConfig {
  enabled: boolean;
  recipients: string[];
  preferences: EventNotificationPreferences;
  updatedAt?: string;
}

interface EventNotificationConfigFormProps {
  config: EventNotificationConfig;
  onChange: (config: EventNotificationConfig) => void;
  onSave: () => void;
  onTest: () => void;
  saving?: boolean;
  testing?: boolean;
  dirty?: boolean;
  /** Device types present in the selected hotel. If empty/undefined, show all sections. */
  hotelDeviceTypes?: string[];
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EventNotificationConfigForm: React.FC<EventNotificationConfigFormProps> = ({
  config,
  onChange,
  onSave,
  onTest,
  saving = false,
  testing = false,
  dirty = false,
  hotelDeviceTypes,
}) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.adminOptions.eventNotifications.${key}`);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  // Determine which sections to show based on hotel device types
  const hasDeviceInfo = hotelDeviceTypes && hotelDeviceTypes.length > 0;
  const showDispenser = !hasDeviceInfo || hotelDeviceTypes.includes('Dispenser');
  const showKiosk = !hasDeviceInfo || hotelDeviceTypes.includes('Kiosk');
  const showOnline = !hasDeviceInfo || hotelDeviceTypes.some(t => ['Mobile', 'OnlineCheckIn', 'Virtual'].includes(t));

  const updateConfig = (partial: Partial<EventNotificationConfig>) => {
    onChange({ ...config, ...partial });
  };

  const updatePreference = (key: keyof EventNotificationPreferences, value: boolean) => {
    onChange({
      ...config,
      preferences: { ...config.preferences, [key]: value },
    });
  };

  const addRecipient = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setEmailError(t('recipients.invalidEmail'));
      return;
    }
    if (config.recipients.includes(email)) {
      setEmailError(t('recipients.duplicateEmail'));
      return;
    }

    updateConfig({ recipients: [...config.recipients, email] });
    setEmailInput('');
    setEmailError('');
  };

  const removeRecipient = (email: string) => {
    updateConfig({ recipients: config.recipients.filter((r) => r !== email) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRecipient();
    }
  };

  const prefSwitch = (
    key: keyof EventNotificationPreferences,
    labelKey: string,
    helperKey: string,
  ) => (
    <Box key={key} sx={{ mb: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={config.preferences[key]}
            onChange={(e) => updatePreference(key, e.target.checked)}
            disabled={!config.enabled}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2">{t(`preferences.${labelKey}`)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`preferences.${helperKey}`)}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0, mb: 0.5 }}
      />
    </Box>
  );

  return (
    <Box>
      {/* Enable toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={config.enabled}
            onChange={(e) => updateConfig({ enabled: e.target.checked })}
          />
        }
        label={<Typography variant="body1" fontWeight={500}>{t('enabled')}</Typography>}
        sx={{ mb: 2 }}
      />

      {!config.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('messages.noConfig')}
        </Alert>
      )}

      {config.enabled && (
        <>
          {/* Recipients */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('recipients.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              placeholder={t('recipients.placeholder')}
              value={emailInput}
              onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
              onKeyDown={handleKeyDown}
              error={!!emailError}
              helperText={emailError}
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              onClick={addRecipient}
              startIcon={<AddIcon />}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              {t('recipients.add')}
            </Button>
          </Box>

          {config.recipients.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('recipients.empty')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {config.recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeRecipient(email)}
                  size="small"
                />
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Preferences */}
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t('preferences.title')}</Typography>

          {/* Dispenser */}
          {showDispenser && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {t('preferences.dispenser')}
              </Typography>
              {prefSwitch('notifyKeyDispensed', 'notifyKeyDispensed', 'notifyKeyDispensedHelper')}
              {prefSwitch('notifyCardLow', 'notifyCardLow', 'notifyCardLowHelper')}
              {prefSwitch('notifyCardEmpty', 'notifyCardEmpty', 'notifyCardEmptyHelper')}
            </>
          )}

          {/* Kiosk */}
          {showKiosk && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
                {t('preferences.kiosk')}
              </Typography>
              {prefSwitch('notifyCheckInCompleted', 'notifyCheckInCompleted', 'notifyCheckInCompletedHelper')}
            </>
          )}

          {/* Online/Mobile */}
          {showOnline && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
                {t('preferences.online')}
              </Typography>
              {prefSwitch('notifyPreCheckInCompleted', 'notifyPreCheckInCompleted', 'notifyPreCheckInCompletedHelper')}
            </>
          )}

          {/* General - always shown */}
          <Typography variant="overline" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
            {t('preferences.general')}
          </Typography>
          {prefSwitch('notifyCriticalErrors', 'notifyCriticalErrors', 'notifyCriticalErrorsHelper')}

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || !dirty}
              startIcon={<SaveIcon />}
              size="small"
            >
              {t('actions.save')}
            </Button>
            <Button
              variant="outlined"
              onClick={onTest}
              disabled={testing || config.recipients.length === 0}
              startIcon={<SendIcon />}
              size="small"
            >
              {t('actions.sendTest')}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};
